import { request } from '@pe-libs/react-components/build/lib/store/api';
import { NextPageContext } from 'next';
import { StateType } from '../../../types';
import { RadiusValues } from '../components/SearchSection/data';
import {
  autocompleteIndustriesResultSchema,
  autocompleteLocationsResultSchema,
  searchResultSchema,
  topSearchesSchema,
} from '../schemas/searchDirectory';

export type GetProfilesType = {
  contextRequest?: NextPageContext['req'];
  keyword: string;
  location: string;
  size: number;
  page: number;
  headers?: Record<string, string>;
  searchRadius: RadiusValues;
};

// Error codes the search/location endpoints return for bad user input (a location that
// doesn't exist, a query that's too long, …). These are valid, expected outcomes — not
// incidents — so we don't want them logged or reported to Sentry.
const EXPECTED_SEARCH_ERROR_CODES = new Set(['LOCATION_NOT_FOUND', 'INDUSTRY_NOT_FOUND']);

// Classifies a search error as expected. Works both inside `request()` (where the raw
// `response` is available, so any 4xx is treated as expected user input) and at call sites
// that only receive the thrown error body (where we match on the `code`). Reporting these on
// every keystroke spams Sentry and leaks memory via its default integrations.
export const isExpectedSearchError = (error: unknown, response?: Response): boolean => {
  const status = response?.status;

  if (typeof status === 'number' && status >= 400 && status < 500) {
    return true;
  }

  const code = (error as { code?: string } | null | undefined)?.code;

  return typeof code === 'string' && EXPECTED_SEARCH_ERROR_CODES.has(code);
};

const searchAPI = (state: StateType) => {
  const locale = state.app.settings?.locale ?? 'de-DE';

  return {
    getProfiles: async ({ contextRequest, keyword, location, size, page, headers, searchRadius }: GetProfilesType) =>
      request({
        locale,
        contextRequest,
        path: `/search/profiles/?query=${encodeURIComponent(keyword)}&locationSearchTerm=${location}&page=${page -
          1}&size=${size}&radius=${searchRadius}`,
        method: 'GET',
        schema: searchResultSchema,
        withAuth: false,
        isExpectedError: isExpectedSearchError,
        headers: headers && {
          'x-forwarded-host': headers.host,
          'user-agent': headers['user-agent'],
          'x-real-ip': headers['x-real-ip'],
          'x-forwarded-proto': headers['x-forwarded-proto'],
          'x-forwarded-for': headers['x-forwarded-for'],
        },
      }),
    getIndustries: async ({ contextRequest, search }: { contextRequest?: NextPageContext['req']; search: string }) =>
      request({
        locale,
        contextRequest,
        path: `/search/industries/autocomplete?query=${search}`,
        method: 'GET',
        schema: autocompleteIndustriesResultSchema,
        withAuth: false,
        isExpectedError: isExpectedSearchError,
      }),
    getLocations: async ({ contextRequest, location }: { contextRequest?: NextPageContext['req']; location: string }) =>
      request({
        locale,
        contextRequest,
        path: `/search/locations/autocomplete?query=${location}`,
        method: 'GET',
        schema: autocompleteLocationsResultSchema,
        withAuth: false,
        isExpectedError: isExpectedSearchError,
      }),
    getStatistics: async ({ contextRequest, size }: { contextRequest?: NextPageContext['req']; size: number }) =>
      request({
        locale,
        contextRequest,
        path: `/search/statistics/?size=${size}`,
        method: 'GET',
        schema: topSearchesSchema,
        withAuth: false,
      }),
  };
};

export default searchAPI;
